import React, {
	useMemo,
} from 'react';
import {
	Empty,
	Flex,
	Table as AntdTable,
	Typography,
} from 'antd';
import {
	Currencies,
	EstimateStatus,
} from '@shared/utils/constants';
import { formatCurrency } from '@shared/utils/currency';
import { formatNumber } from '@shared/utils/formatNumber';
import { calculateTotal, round } from '@shared/utils/math';
import { capitalize } from '@shared/utils/string';
import getIntakeFDDNumbers, { IntakeFDDCalculation } from '@shared/utils/getIntakeFDDNumbers';
import { useEstimate } from '@client/screens/estimates/details/context/EstimatorContext';
import Table from '@client/components/Table/Table';
import NumericInput from '@client/components/NumericInput';
import { useCargoProvider } from '@client/screens/estimates/details/context/CargoContext';
import sharedStyles from '@client/screens/estimates/details/styles/shared.module.css';

enum FreightType {
	FREIGHT = 'freight',
	OVERAGE = 'overage',
	DEADFREIGHT = 'deadfreight',
}

type Row = {
	key: FreightType;
	type: FreightType;
	rate: number;
	quantity: number;
	gross: number;
};

type Props = {
	cargoId: number;
};

const IntakeCalculatorFreightDetails = ({ cargoId }: Props) => {
	const { estimate } = useEstimate();
	const {
		cargos,
		onUpdateCargo,
	} = useCargoProvider();

	const cargo = useMemo(() => cargos.find((c) => c.id === cargoId), [cargos, cargoId]);

	const intakeFDDCalculation: IntakeFDDCalculation | null = useMemo(() => {
		if (!estimate || cargo == null) {
			return null;
		}

		const totalLoaded = calculateTotal(
			estimate.portRotationEntries.filter((entry) => entry.cargoId === cargo.id),
			(entry) => entry.cargoLoaded ?? 0,
		);

		return getIntakeFDDNumbers(cargo, totalLoaded, estimate.currency);
	}, [cargo, estimate]);

	const freightData = useMemo(() => {
		if (
			!cargo ||
			!intakeFDDCalculation
		) {
			return [];
		}

		return [
			{
				key: 'freight' as FreightType,
				type: 'freight' as FreightType,
				rate: cargo.freightRate ?? 0,
				quantity: intakeFDDCalculation.freightQuantity,
				gross: intakeFDDCalculation.freightAmount,
			},
			{
				key: 'overage' as FreightType,
				type: 'overage' as FreightType,
				rate: cargo.overageAmount ?? 0,
				quantity: intakeFDDCalculation.overageQuantity,
				gross: intakeFDDCalculation.overageAmount,
			},
			{
				key: 'deadfreight' as FreightType,
				type: 'deadfreight' as FreightType,
				rate: cargo.deadfreightAmount ?? 0,
				quantity: intakeFDDCalculation.deadfreightQuantity,
				gross: intakeFDDCalculation.deadfreightAmount,
			},
		];
	}, [cargo, intakeFDDCalculation]);

	const totals = useMemo(() => {
		const totalQuantity = freightData.reduce((acc, row) => acc + row.quantity, 0);
		const totalRevenue = freightData.reduce((acc, row) => acc + row.gross, 0);

		return { totalQuantity, totalRevenue };
	}, [freightData]);

	if (cargo == null) {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description="No data"
			/>
		);
	}

	return (
		<Flex vertical>
			<Typography.Title level={4}>Freight, overage & deadfreight</Typography.Title>
			<Table<Row>
				bordered
				data-testid="intakeCalculatorFreightDetails"
				columns={[
					{
						title: 'Type',
						dataIndex: 'type',
						width: 200,
						onCell: () => ({ className: sharedStyles.calculated }),
						render: (s) => capitalize(s),
					},
					{
						title: 'Quantity',
						dataIndex: 'quantity',
						width: 100,
						onCell: () => ({ className: sharedStyles.calculated }),
						render(value) {
							return `${formatNumber(value, { separateThousands: true }, 2)} MT`;
						},
					},
					{
						title: 'Rate',
						dataIndex: 'rate',
						width: 100,
						render: (_, row) => {
							let valueToUse: number | null = 0;
							let key: string = '';
							switch (row.type) {
								default:
								case FreightType.FREIGHT:
									valueToUse = cargo.freightRate;
									key = 'freightRate';
									break;
								case FreightType.OVERAGE:
									valueToUse = cargo.overageAmount;
									key = 'overageAmount';
									break;
								case FreightType.DEADFREIGHT:
									valueToUse = cargo.deadfreightAmount;
									key = 'deadfreightAmount';
									break;
							}

							return (
								<NumericInput
									fullWidth
									disabled={
										estimate?.isTceLocked ||
										estimate?.isNetResultLocked ||
										estimate?.status === EstimateStatus.Fixed
									}
									variant="borderless"
									value={round(valueToUse ?? 0, 6)}
									suffix={cargo?.currency}
									onChange={(value) => {
										onUpdateCargo(cargo.id, {
											[key]: value,
										});
									}}
								/>
							);
						}
					},
					{
						title: 'Gross',
						dataIndex: 'gross',
						width: 100,
						onCell: () => ({ className: sharedStyles.calculated }),
						render(value) {
							return formatCurrency(value, estimate?.currency ?? Currencies.USD);
						},
					},
				]}
				dataSource={freightData}
				size="small"
				pagination={false}
				summary={() => (
					<AntdTable.Summary.Row style={{ background: '#f0f5ff' }}>
						<AntdTable.Summary.Cell index={0}>
							<b>
								Total Expected Cargo
							</b>
						</AntdTable.Summary.Cell>
						<AntdTable.Summary.Cell index={1}>
							<b>
								{`${formatNumber(totals.totalQuantity, { separateThousands: true }, 2)} MT`}
							</b>
						</AntdTable.Summary.Cell>
						<AntdTable.Summary.Cell index={2} />
						<AntdTable.Summary.Cell index={3}>
							<b>
								{formatCurrency(totals.totalRevenue, estimate?.currency ?? Currencies.USD)}
							</b>
						</AntdTable.Summary.Cell>
					</AntdTable.Summary.Row>
				)}
			/>
		</Flex>
	);
};

export default IntakeCalculatorFreightDetails;
